/**
 * AuthLayout.tsx
 *
 * The universal root wrapper component for every ACUL screen.
 *
 * WHAT IT DOES
 * ------------
 * Renders the split two-column layout detected from example/mvp/login.html:
 *   LEFT  — white form panel (scrollable, content injected via `children`)
 *   RIGHT — dark slate auth-showcase panel with benefit carousel (static)
 *
 * DESIGN ORIGIN
 * -------------
 * Extracted from the Sprint-7 Direction-B refresh in example/mvp/login.html.
 * The layout uses --portal-* CSS custom properties defined in
 * src/assets/styles/auth.css, which is imported once here and therefore
 * available to every screen that wraps itself in AuthLayout.
 *
 * CSS VARIABLES: see src/assets/styles/auth.css
 * FONT FILES:    TODO – copy example/mvp/assets/fonts/ → public/fonts/ and
 *                       add @font-face rules to auth.css (or serve from CDN).
 *
 * HOW TO USE
 * ----------
 * Every screen must wrap its content in <AuthLayout>:
 *
 *   return (
 *     <AuthLayout title="Log in" signupVariant={false}>
 *       <IdentifierForm />
 *     </AuthLayout>
 *   );
 *
 * EXTENDING
 * ---------
 * - Pass `signupVariant` to switch to top-aligned, wider left panel.
 * - Swap the `showcaseSlides` array to change right-panel benefit cards.
 * - The carousel JS is handled by the AuthShowcaseCarousel sub-component.
 *
 * Auth0 / ACUL note
 * -----------------
 * This component has ZERO knowledge of Auth0 SDK.  It is purely presentational.
 */

import React from "react";

import "../../assets/styles/auth.css";

import benefitEquifax from "../../assets/images/benefit-equifax.jpg";
import benefitFox from "../../assets/images/benefit-fox.jpg";
import benefitStatefarm from "../../assets/images/benefit-statefarm.jpg";
import benefitTaxhawk from "../../assets/images/benefit-taxhawk.jpg";
import brandEquifax from "../../assets/images/brand-equifax.png";
import brandFox from "../../assets/images/brand-fox.png";
import brandStatefarm from "../../assets/images/brand-statefarm.png";
import brandTaxhawk from "../../assets/images/brand-taxhawk.svg";
import vsWordmark from "../../assets/images/vs-wordmark.svg";

import { AuthShowcaseCarousel } from "./AuthShowcaseCarousel";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthLayoutProps {
  /** Page title rendered as <h1> inside the form panel */
  title: string;
  /**
   * When true the form panel shifts to top-aligned with a 3-step stepper
   * header, matching the signup.html mockup.  Default: false (login mode).
   */
  signupVariant?: boolean;
  /**
   * Optional element inserted above the <h1> (e.g. the signup stepper).
   * Login mode ignores this.
   */
  headerSlot?: React.ReactNode;
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Carousel slide data (sourced from example/assets/img/*)
// ---------------------------------------------------------------------------

const SHOWCASE_SLIDES = [
  {
    img: benefitEquifax,
    brandLogo: brandEquifax,
    brandAlt: "Equifax",
    invert: false,
    caption: "Credit & ID monitoring + $1M insurance",
  },
  {
    img: benefitTaxhawk,
    brandLogo: brandTaxhawk,
    brandAlt: "TaxHawk",
    invert: true,
    caption: "Free federal and state taxes",
  },
  {
    img: benefitFox,
    brandLogo: brandFox,
    brandAlt: "Fox Nation",
    invert: false,
    caption: "2 years of free subscription",
  },
  {
    img: benefitStatefarm,
    brandLogo: brandStatefarm,
    brandAlt: "State Farm",
    invert: true,
    caption: "24/7 roadside assistance",
  },
];

// ---------------------------------------------------------------------------
// VS Brand Mark (inline SVG — no extra HTTP request)
// ---------------------------------------------------------------------------

function VsBrandMark() {
  return (
    <span className="portal-brand-mark" aria-hidden="true">
      <svg viewBox="0 0 67 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M44.3561 21.6433L33.1255 38.6858L11.5864 6.22082H19.1681L33.0682 27.2548L36.7909 21.6828L36.8026 21.6141L22.4924 0H0L33.0488 50L51.8587 21.6299L41.6796 6.22082H54.549L51.913 10.2778L55.6181 15.9019L66.1354 0H30.0935L44.3561 21.6433Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

// ---------------------------------------------------------------------------
// AuthLayout component
// ---------------------------------------------------------------------------

export function AuthLayout({
  title,
  signupVariant = false,
  headerSlot,
  children,
}: AuthLayoutProps) {
  const leftClass = [
    "auth-layout__left",
    signupVariant ? "auth-layout__left--signup" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const wrapClass = [
    "auth-layout__form-wrap",
    signupVariant ? "auth-layout__form-wrap--signup" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="auth-layout">
      {/* ── LEFT: form panel ── */}
      <section className={leftClass}>
        <div className={wrapClass}>
          {/* Brand lockup */}
          <a href="/" className="portal-brand" aria-label="Veteran Strong home">
            <VsBrandMark />
            <img
              className="portal-brand-word"
              src={vsWordmark}
              alt="Veteran Strong"
            />
          </a>

          {/* Optional header slot (signup stepper goes here) */}
          {signupVariant && headerSlot && (
            <div className="signup-header-slot">{headerSlot}</div>
          )}

          {/* Page title */}
          <h1
            className={[
              "auth-page-title",
              !signupVariant ? "auth-page-title--eyebrow" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {title}
          </h1>

          {/* Screen-specific content */}
          {children}
        </div>
      </section>

      {/* ── RIGHT: auth showcase panel ── */}
      <aside
        className="auth-showcase auth-showcase--sticky"
        aria-label="Member benefits"
      >
        <div className="auth-showcase__inner">
          <div className="auth-showcase__head">
            <p className="auth-showcase__eyebrow">Member benefits</p>
            <h2 className="auth-showcase__pitch">
              The most advanced financial platform designed for Veterans
            </h2>
          </div>

          <AuthShowcaseCarousel slides={SHOWCASE_SLIDES} />
        </div>
      </aside>
    </div>
  );
}

export default AuthLayout;
